import IdeaProperty from "@/types/IdeaProperty";
import Workflow from "@/types/Workflow";
import Domain from "@/types/Domain";

/**
 * As perguntas do acesso por item, na ordem em que o servidor as avalia — cada fórmula só lê o acesso
 * do próprio item que vem antes dela (`editable` lê `_visible`). O dono da regra é o servidor
 * (`lunedi/src/domain/access.ts`); aqui é o que a tela de design oferece.
 */
export const ACCESS_KINDS = ['visible', 'editable', 'deletable', 'creatable'] as const
export type AccessKind = typeof ACCESS_KINDS[number]

/**
 * A chave do rótulo de cada pergunta, lida pelo editor e pela visualização. "Visível quando", e não
 * "Quem vê" (escolha do usuário): a fórmula pode depender de outros campos, e não só de pessoas.
 */
export const ACCESS_LABELS = {
    visible: 'access.visible',
    editable: 'access.editable',
    deletable: 'access.deletable',
    creatable: 'access.creatable'
} as const satisfies Record<AccessKind, string>

/**
 * O ícone de cada pergunta: a pessoa com a ação que ela libera (pedido do usuário) — a pergunta é sempre
 * QUEM pode. Lido pelo editor de acesso e pela ficha do campo no design.
 */
export const ACCESS_ICONS: Record<AccessKind, string> = {
    visible: 'mdi-account-eye-outline',
    editable: 'mdi-account-edit-outline',
    deletable: 'mdi-account-remove-outline',
    creatable: 'mdi-account-plus-outline'
}

/** O acesso de uma coleção: uma fórmula de Sim/Não por pergunta. Ausente é "todos podem". */
export type IdeaAccess = { [kind in AccessKind]?: string }

/**
 * As perguntas de um CAMPO — quem vê, quem edita (pedido do usuário) —, um subconjunto das do item, com
 * os mesmos rótulos (`ACCESS_LABELS`). O dono da regra é o servidor (`PROPERTY_ACCESS_KINDS`).
 */
export const PROPERTY_ACCESS_KINDS = ['visible', 'editable'] as const satisfies readonly AccessKind[]
export type PropertyAccessKind = typeof PROPERTY_ACCESS_KINDS[number]

/** O acesso de uma propriedade: ausente, quem vê o item vê e edita o campo. */
export type PropertyAccess = { [kind in PropertyAccessKind]?: string }

/**
 * O que quem olha pode fazer com um item, como a leitura o devolve (`_access`). Ausente é "pode". Os
 * campos restritos vêm por alias, só eles: `hidden` (não vê — o valor nem veio) e `locked` (vê e não edita).
 */
export type ItemAccess = { editable?: boolean, deletable?: boolean, hidden?: string[], locked?: string[] }

type Accessed = { _access?: ItemAccess }

/** A fórmula da coleção deixa quem olha editar este item? Sem `_access`, não há fórmula — deixa. */
export function itemEditable(item: Accessed): boolean {
    return !(item._access && item._access.editable === false)
}

/** E excluir? Como o `itemEditable`, pela resposta do `deletable`. */
export function itemDeletable(item: Accessed): boolean {
    return !(item._access && item._access.deletable === false)
}

/**
 * Quem olha vê este campo neste item? Um dono para todas as telas que mostram campos — a ficha, o
 * formulário, a grade, o Kanban: o campo oculto não aparece em lugar nenhum (pedido do usuário). Quem
 * decide é o servidor, pela fórmula do campo; sem `_access`, vê.
 */
export function fieldVisible(item: Accessed | undefined, property: { alias: string }): boolean {
    return !(item && item._access && item._access.hidden && item._access.hidden.includes(property.alias))
}

/** E edita? O campo travado aparece com o cadeado; o do item que não se edita, também. */
export function fieldEditable(item: Accessed | undefined, property: { alias: string }): boolean {
    if (!item || !item._access) return true
    const {hidden = [], locked = []} = item._access
    return itemEditable(item) && !hidden.includes(property.alias) && !locked.includes(property.alias)
}

type Idea = {
    name: string
    pluralName: string
    alias: string
    /** Alias do fluxo. O fluxo em si vem resolvido em `_workflow`. */
    workflow?: string
    /**
     * O fluxo já resolvido, quando a resposta o pediu: o $lookup de `systems/:alias`
     * o traz para toda coleção do workspace, e o `meta: ["workflow"]` do $ideas.get
     * para uma coleção só.
     */
    _workflow?: Workflow
    /**
     * Os domínios dos atributos de seleção, quando a resposta os pediu — só o
     * `meta: ["domains"]` do $ideas.get os traz. Quem tem o workspace usa
     * `workspace.domains`; isto serve a quem não o tem, como o modal de criar item.
     */
    _domains?: Domain[]
    properties: IdeaProperty[]
    /** Quem vê, edita, exclui e cria os itens — ver `IdeaAccess`. */
    access?: IdeaAccess
    /**
     * Se quem olha pode criar nesta coleção, quando a resposta não depende do item — o servidor
     * avalia o `creatable` para ele. Ausente: pode, ou depende do item, e quem decide é o salvar.
     */
    _creatable?: boolean
}

/**
 * A coleção tem alguma fórmula de acesso — a do item, que decide se ele se vê, se edita, se exclui?
 * Só ela: é a que pode tirar o item de quem o edita (o aviso do `InstanceEdit`). A dos campos é o
 * `hasFieldAccess`.
 */
export function hasAccessRules(idea: Idea): boolean {
    return Object.keys(writtenAccess(idea.access)).length > 0
}

/** Algum campo da coleção tem fórmula de quem vê ou de quem edita? */
export function hasFieldAccess(idea: Idea): boolean {
    return (idea.properties || []).some(property => !!property.access)
}

/**
 * Só as perguntas com fórmula, aparada — a régua do servidor (`accessOf` no `Idea` de lá), que
 * grava assim. É por ela que o diálogo compara: uma pergunta apagada vira `''` no rascunho, e sem
 * isto contaria como mudança.
 */
export function writtenAccess(access: IdeaAccess | undefined): IdeaAccess {
    const written = ACCESS_KINDS.filter(kind => access && access[kind] && access[kind]!.trim())
    return Object.fromEntries(written.map(kind => [kind, access![kind]!.trim()]))
}

export default Idea